import { Button, Form, Input, List, Modal, Upload } from "antd";
import { useContext, useEffect, useState } from "react";
import { PaperClipOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import moment from "moment";

import Editor from "./Editor";
import { useAlert } from "../context/AlertProvider";
import { EmailContext } from "../context/EmailContext";
import CopyText from "../components/web2lead/CopyText";
import copy from "../assets/images/webToLead/copy.png";
import { sendEmail } from "../actionCreator/emailSetupActionCreater";
import SubscriptionModal from "../components/web2lead/SubscriptionModal";
import { getSubscriptionUserInfo } from "../utils/utils";
import { AuthContext } from "../context/AuthContext";

const EmailForm = ({ children, recipientEmail, empId, payload, callback }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { showAlert } = useAlert();
  const { isEmailSetupCreate } = useContext(AuthContext);
  const subscription = getSubscriptionUserInfo();
  const currentDate = moment();
  const validToDate = moment(subscription?.validTo);
  const isExpired = validToDate.isBefore(currentDate);
  const { isEmailConfigured, emailConfiguration } = useContext(EmailContext);
  const [fileList, setFileList] = useState([]);
  const [embeddedImgs, setEmbeddedImgs] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [subModal, setSubModal] = useState(false);

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
    setError();
    setFileList([]);
    setEmbeddedImgs([]);
  };

  const handleFilesChange = ({ file, fileList }) => {
    const isLt10MB = file.size / 1024 / 1024 < 10;
    if (!isLt10MB) {
      showAlert("error", `${file.name} is larger than 10MB!`);
    } else {
      setFileList(fileList);
    }
  };

  const handleRemove = (file) => {
    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
  };

  const onOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (emailConfiguration?.email || recipientEmail) {
      form.setFieldsValue({
        from: emailConfiguration?.email,
        to: recipientEmail,
      });
    }
  }, [emailConfiguration, recipientEmail, form, isOpen]);

  useEffect(() => {
    if (payload?.subject) {
      form.setFieldsValue({
        subject: payload?.subject,
      });
    }
  }, [payload, form]);

  const onSubmit = async (values) => {
    setLoading(true);
    const data = new FormData();

    const mailBody = addSrcToImages(values.body, embeddedImgs);
    data.append(
      "request",
      JSON.stringify({
        from: values.from,
        to: values.to,
        subject: values.subject,
        body: mailBody,
        ...payload,
        context: {
          employee_id: empId,
        },
      })
    );

    // Append multiple files
    fileList.forEach((file) => {
      data.append("attachments", file.originFileObj);
    });

    embeddedImgs.forEach((img) => {
      const imgId = img?.id; // Assuming each image has a unique 'id'

      // Check if the HTML content includes the image ID
      if (mailBody.includes(imgId)) {
        data.append("inlineAttachments", img?.file);
      }
    });

    const res = await dispatch(sendEmail(data));

    if (res?.status === 200) {
      showAlert("success", `Email sent successfully!`);
      handleCancel();
      callback && callback();
    } else {
      if (res?.response?.status === 403) {
        showAlert(
          "warning",
          "Permission Denied",
          "You do not have permission to send email."
        );
        handleCancel();
      } else {
        showAlert(
          "error",
          res?.response?.status === 500
            ? "Something went wrong. Please try again."
            : res?.response?.data?.title
        );
        setError(
          res?.response?.status === 500
            ? "Something went wrong. Please try again."
            : res?.response?.data?.title
        );
      }
    }
    setLoading(false);
  };

  const addSrcToImages = (htmlString, imagesArray) => {
    return htmlString.replace(
      /<img\s+([^>]*data-ck-upload-id="([^"]+)")/g,
      (match, fullAttribute, uploadId) => {
        const image = imagesArray.find((img) => img.id === uploadId);
        if (image) {
          return `<img ${fullAttribute} src="${image.cidUrl}"`; // Add src dynamically
        }
        return match; // Return unchanged if no match found
      }
    );
  };

  return (
    <>
      <SubscriptionModal
        isOpen={subModal}
        handleCancel={() => setSubModal(false)}
      />
      <div
        onClick={() => {
          if (!subscription?.id || isExpired) {
            setSubModal(true);
          } else {
            if (!isEmailSetupCreate) {
              showAlert(
                "warning",
                "Permission Denied",
                "You do not have permission to send email."
              );
            } else {
              if (!isEmailConfigured) {
                navigate("/settings/email-setup");
              } else {
                onOpen();
              }
            }
          }
        }}
      >
        {children}
      </div>
      <Modal
        open={isOpen}
        footer={null}
        className="delete-popup email-form"
        centered
        width={650}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <Form
          key={isOpen}
          form={form}
          layout="vertical"
          className="border overflow-x-hidden rounded py-20 heading13px"
          style={{ maxWidth: 650 }}
          onFinish={onSubmit}
        >
          {!!error && (
            <p className="text-error text-center pb-2 px-2 mx-2">{error}</p>
          )}
          <div className="px-2">
            <Form.Item
              name="from"
              rules={[
                {
                  type: "email",
                  message: "Please enter valid E-mail!",
                },
                { required: true, message: "From is required" },
              ]}
              className="mb0"
            >
              <div className="d-flex align-items-center w-100 border-bottom mt5">
                <label className="heading13px">From:</label>
                <Input
                  disabled
                  defaultValue={emailConfiguration?.email}
                  variant="borderless"
                  className="text-color-030334"
                />
                <div style={{ marginRight: 30 }}>
                  <CopyText
                    text={emailConfiguration?.email}
                    content={
                      <img
                        src={copy}
                        alt=""
                        style={{ width: 15, height: 15 }}
                        className="cursor-pointer"
                      />
                    }
                  />
                </div>
              </div>
            </Form.Item>
            <Form.Item
              name="to"
              className="mb0"
              rules={[
                {
                  type: "email",
                  message: "Please enter valid E-mail!",
                },
                { required: true, message: "Recipients is required" },
              ]}
            >
              <div className="d-flex align-items-center border-bottom w-100 mt5">
                <label className="heading13px">Recipients:</label>
                <Input
                  disabled
                  defaultValue={recipientEmail}
                  variant="borderless"
                  className="text-color-030334"
                />
              </div>
            </Form.Item>
            {/* <Form.List name="cc">
              {(fields, { add, remove }) => (
                <div className="px-2">
                  <label className="heading13px">CC:</label>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="d-flex align-items-center border-bottom w-100 mt5"
                    >
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[
                          {
                            type: "email",
                            message: "Please enter a valid email!",
                          },
                        ]}
                        className="mb0 flex-grow-1"
                      >
                        <Input
                          variant="borderless"
                          placeholder="Enter CC email"
                        />
                      </Form.Item>
                      <DeleteOutlined
                        onClick={() => remove(name)}
                        style={{
                          color: "red",
                          cursor: "pointer",
                          marginLeft: 10,
                        }}
                      />
                    </div>
                  ))}
                  <Button type="link" onClick={() => add()}>
                    + Add CC
                  </Button>
                </div>
              )}
            </Form.List> */}

            {/* <Form.List name="bcc">
              {(fields, { add, remove }) => (
                <div className="px-2">
                  <label className="heading13px">BCC:</label>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="d-flex align-items-center border-bottom w-100 mt5"
                    >
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[
                          {
                            type: "email",
                            message: "Please enter a valid email!",
                          },
                        ]}
                        className="mb0 flex-grow-1"
                      >
                        <Input
                          variant="borderless"
                          placeholder="Enter BCC email"
                        />
                      </Form.Item>
                      <DeleteOutlined
                        onClick={() => remove(name)}
                        style={{
                          color: "red",
                          cursor: "pointer",
                          marginLeft: 10,
                        }}
                      />
                    </div>
                  ))}
                  <Button type="link" onClick={() => add()}>
                    + Add BCC
                  </Button>
                </div>
              )}
            </Form.List> */}
            <Form.Item
              name="subject"
              rules={[{ required: true, message: "Subject is required" }]}
              className="mb0"
            >
              <div className="d-flex align-items-center border-bottom w-100 mt5">
                <label className="heading13px">Subject:</label>
                <Input
                  defaultValue={payload?.subject}
                  disabled={!!payload?.subject}
                  variant="borderless"
                  className="text-color-030334"
                />
              </div>
            </Form.Item>
          </div>
          <Form.Item
            name="body"
            rules={[{ required: true, message: "Content is required" }]}
            className="mt5 mb0 email-content-error"
          >
            <Editor
              setEmbeddedImgs={setEmbeddedImgs}
              onChange={(data) => form.setFieldsValue({ body: data })}
            />
          </Form.Item>
          {!!fileList?.length && (
            <div className="px-2">
              <List
                size="small"
                className="attachment-list mt10"
                dataSource={fileList}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <DeleteOutlined
                        key="delete"
                        onClick={() => handleRemove(item)}
                        style={{ color: "red", cursor: "pointer" }}
                      />,
                    ]}
                    style={{ padding: "8px 0" }}
                    className="heading12px"
                  >
                    {item.name}
                  </List.Item>
                )}
              />
            </div>
          )}
          <div
            className="d-flex border-top pt20 justify-content-end align-items-center mt-4 mx-2"
            style={{ gap: 20 }}
          >
            <div className="heading12px text-gray">
              Maximum allowed file size is 10 MB
            </div>
            <Upload
              multiple
              fileList={fileList}
              beforeUpload={() => false} // Prevent auto-upload
              onChange={handleFilesChange}
              showUploadList={false}
            >
              <PaperClipOutlined className="heading18px cursor-pointer" />
            </Upload>
            <Button
              loading={loading}
              type="primary"
              htmlType="submit"
              className="theme-btn"
            >
              Send{" "}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default EmailForm;
